import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Camera, RotateCcw, Sparkles, Check } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Wave } from "../ui/decor";
import { CalorieDial } from "../visuals/CalorieDial";
import { RATES } from "../../content/earnings";

// A deterministic week of morning losses (kg), so the demo tells a clean story.
const LOSSES = [0.3, 0.2, 0.4, 0.2, 0.3, 0.2, 0.4];
const START = 82.0;
const WEEK_TARGET = 7700;

type Entry = {
  day: number;
  weight: number;
  allTime: number;
  threeDay: number;
  time: number;
  total: number;
};

export function PhoneDemo() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const day = entries.length;
  const done = day >= LOSSES.length;

  const bank = useMemo(
    () => entries.reduce((sum, e) => sum + e.total, 0),
    [entries]
  );
  const weight = entries.length ? entries[entries.length - 1].weight : START;
  const last = entries[entries.length - 1];

  const logDay = () => {
    if (done) return;
    const delta = LOSSES[day];
    const newWeight = +(weight - delta).toFixed(1);
    const allTime = Math.round(delta * RATES.allTimeLowPerKg);
    const threeDay = Math.round(delta * RATES.threeDayAvgPerKg);
    const time = RATES.timePer24h;
    setEntries((e) => [
      ...e,
      { day: day + 1, weight: newWeight, allTime, threeDay, time, total: allTime + threeDay + time },
    ]);
  };

  const reset = () => setEntries([]);

  return (
    <section id="demo" className="relative overflow-hidden bg-teal-deep">
      {/* Cream waves melt the teal playground into the page above and below. */}
      <Wave fill="#FFF6EC" flip />

      <div className="container-x py-14 sm:py-20">
        <SectionHeading
          light
          eyebrow="Try the demo"
          title={
            <>
              Feel the <span className="marker text-navy">signature second</span>
            </>
          }
          sub="Tap to log a morning weigh-in and watch the bank jump. This is the moment the whole app is built around — no download needed to try it."
        />

        <div className="mx-auto mt-14 grid max-w-5xl items-center gap-12 lg:grid-cols-2">
          {/* Explanation + controls */}
          <Reveal>
            <div className="order-2 lg:order-1">
              <ol className="space-y-4">
                {[
                  ["Weigh in", "Photograph your scale or type the number — under three seconds."],
                  ["The bank jumps", "Your balance counts up instantly, before you've logged a bite."],
                  ["Read the receipt", "See exactly which streams paid out, in plain numbers."],
                ].map(([t, b], i) => (
                  <li
                    key={t}
                    className={`card-pop flex items-center gap-4 !shadow-pop-sm p-4 sm:p-5 ${
                      i % 2 === 0 ? "rotate-[0.6deg]" : "-rotate-[0.6deg]"
                    }`}
                  >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border-[3px] border-navy bg-sun font-display text-lg font-extrabold text-navy">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-display font-extrabold text-navy">{t}</p>
                      <p className="text-sm text-charcoal/70">{b}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button
                  onClick={logDay}
                  disabled={done}
                  className="btn-sun px-8 py-4 text-lg disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Camera className="h-5 w-5" />
                  {done ? "Week complete" : day === 0 ? "Log your first weigh-in" : `Log day ${day + 1}`}
                </button>
                {day > 0 && (
                  <button onClick={reset} className="btn-ghost px-6 py-3.5">
                    <RotateCcw className="h-4 w-4" /> Reset
                  </button>
                )}
              </div>
              <p className="mt-4 text-xs font-semibold text-white/90">
                A simulation of the in-app mechanic — real figures follow your own scale.
              </p>
            </div>
          </Reveal>

          {/* Phone */}
          <Reveal delay={1}>
            <div className="order-1 mx-auto lg:order-2">
              <div className="relative mx-auto w-[300px]">
                {/* Device frame — pop treatment, straightens on hover */}
                <div className="relative rotate-2 rounded-[2.75rem] border-[3px] border-navy bg-navy p-3 shadow-pop transition-transform duration-300 hover:rotate-0">
                  <div className="absolute left-1/2 top-3 z-20 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-navy" />
                  <div className="relative overflow-hidden rounded-[2.25rem] bg-gradient-to-b from-white to-cream px-4 pb-5 pt-9">
                    {/* Status row */}
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-display text-sm font-extrabold text-navy">
                        Fit<span className="text-teal">Buddy</span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border-2 border-navy bg-sun px-2.5 py-0.5 text-[11px] font-extrabold text-navy">
                        <Flame className="h-3 w-3 text-coral" />
                        <span className="num">{day}</span>-day
                      </span>
                    </div>

                    {/* Bank card — the signature dial on its own chunky widget */}
                    <div className="rounded-3xl border-2 border-navy bg-white p-2 shadow-pop-xs">
                      <div className="flex justify-center">
                        <CalorieDial value={bank} max={WEEK_TARGET} size={168} label="CAL THIS WEEK" theme="light" />
                      </div>
                      {/* Weight readout */}
                      <div className="mb-1.5 flex items-center justify-center gap-2 text-sm">
                        <span className="text-charcoal/55">Weight</span>
                        <span className="num font-bold text-navy">{weight.toFixed(1)} kg</span>
                        {last && (
                          <motion.span
                            key={weight}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="num rounded-full bg-teal-mist px-1.5 text-xs font-bold text-teal"
                          >
                            ▼ {LOSSES[last.day - 1].toFixed(1)}
                          </motion.span>
                        )}
                      </div>
                    </div>

                    {/* Receipt — a sunny payout slip */}
                    <div className="mt-3 min-h-[104px] rounded-2xl border-2 border-navy bg-sun-pale p-3">
                      <AnimatePresence mode="wait">
                        {last ? (
                          <motion.div
                            key={last.day}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className="space-y-1.5"
                          >
                            {[
                              ["All-time low", last.allTime],
                              ["3-day average", last.threeDay],
                              ["Showing up", last.time],
                            ].map(([l, v]) => (
                              <div key={l as string} className="flex justify-between text-xs">
                                <span className="font-medium text-charcoal/70">{l}</span>
                                <span className="num font-bold text-teal">
                                  +{(v as number).toLocaleString()}
                                </span>
                              </div>
                            ))}
                            <div className="!mt-2 flex justify-between border-t-2 border-dashed border-navy/25 pt-2 text-xs">
                              <span className="font-display font-extrabold text-navy">Earned</span>
                              <span className="num font-bold text-violet">
                                +{last.total.toLocaleString()}
                              </span>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="grid h-[92px] place-items-center text-center text-xs font-medium text-charcoal/60"
                          >
                            Tap “Log your first weigh-in” to see your bank grow.
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* In-phone button */}
                    <button
                      onClick={logDay}
                      disabled={done}
                      className="btn-primary mt-3 w-full py-2.5 text-sm !shadow-pop-xs disabled:opacity-50"
                    >
                      {done ? (
                        <>
                          <Check className="h-4 w-4" /> Nice week
                        </>
                      ) : (
                        <>
                          <Camera className="h-4 w-4" /> Log weigh-in
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Sticker overlapping the phone corner */}
                <span
                  aria-hidden
                  className="sticker absolute -right-6 -top-4 z-30 -rotate-6"
                >
                  The bank jumps
                </span>

                {/* Completion badge — sun sticker moment */}
                <AnimatePresence>
                  {done && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute -bottom-4 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap"
                    >
                      <span className="sticker rotate-2">
                        <Sparkles className="h-4 w-4 text-violet" />
                        <span className="num font-bold">
                          {bank.toLocaleString()}
                        </span>{" "}
                        banked in a week
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <Wave fill="#FFF6EC" />
    </section>
  );
}
